/**
 * BlueprintCard — compact template card for the Blueprints Templates tab.
 *
 * Used by:
 *   - BlueprintsTemplateListPage (T6) — the grouped template grid + list view
 *   - FavoritesAndRecent (T10) — the favorites + recently-used carousels
 *
 * Exports:
 *   BlueprintCard({ template, onChoose })       — grid-mode card
 *   BlueprintListRow({ template, onChoose })    — list-mode row (compact)
 *   BlueprintCardProps                            — prop interface (shared)
 */

import type { CSSProperties, ReactNode } from 'react';
import {
  Badge,
  Box,
  Button,
  ButtonIcon,
  Card,
  Flex,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@backstage/ui';
import { RiStarFill, RiStarLine } from '@remixicon/react';
import {
  humanizeEntityRef,
  useStarredEntity,
} from '@backstage/plugin-catalog-react';
import { parseEntityRef } from '@backstage/catalog-model';
import type { TemplateEntityV1beta3 } from '@backstage/plugin-scaffolder-common';
import { BLUEPRINT_CATEGORIES } from './blueprintCategories';

// ── Tags ──────────────────────────────────────────────────────────────────────

type TagTone = 'category' | 'lifecycle' | 'neutral';

/**
 * A small tag. Curated categories sit on the brand surface, the lifecycle is
 * outlined in the brand colour and free-form tags are neutral, as on the
 * original cards.
 */
const TAG_SURFACE: Record<TagTone, CSSProperties> = {
  category: {
    backgroundColor: 'var(--make-bg-brand-subtle)',
    border: '1px solid var(--make-border-brand-subtle)',
    color: 'var(--make-fg-brand)',
  },
  lifecycle: {
    border: '1px solid var(--make-fg-brand)',
    color: 'var(--make-fg-brand)',
  },
  neutral: { border: '1px solid var(--bui-border-2)' },
};

function Tag({ children, tone }: { children: ReactNode; tone: TagTone }) {
  return (
    <Badge style={{ flexShrink: 0, ...TAG_SURFACE[tone] }}>
      <Text
        variant="body-small"
        weight={tone === 'neutral' ? 'regular' : 'bold'}
        color={tone === 'neutral' ? 'secondary' : undefined}
        style={{
          // Trim the line box to the capitals so the text sits in the middle.
          textBox: 'trim-both cap alphabetic',
          ...(tone === 'neutral' ? {} : { color: 'inherit' }),
        }}
      >
        {children}
      </Text>
    </Badge>
  );
}

const WRAP = { flexWrap: 'wrap' } as const;

// ── Helpers ───────────────────────────────────────────────────────────────────

const CATEGORY_IDS = new Set(BLUEPRINT_CATEGORIES.map(c => c.id));

/** Format a spec.owner entityRef string for display. Returns empty string on failure. */
function formatOwner(owner: string | undefined): string {
  if (!owner) return '';
  try {
    const parsed = parseEntityRef(owner, {
      defaultKind: 'group',
      defaultNamespace: 'default',
    });
    return humanizeEntityRef(parsed, {
      defaultKind: 'group',
      defaultNamespace: 'default',
    });
  } catch {
    // owner is not a valid entityRef — return as-is (stripped of kind prefix)
    return owner.replace(/^[^:]+:/, '');
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

export interface BlueprintCardProps {
  template: TemplateEntityV1beta3;
  /** Called when the user clicks "Choose". Parent handles route navigation. */
  onChoose: () => void;
}

/**
 * Compact template card for the Blueprints hub.
 *
 * Renders a brand-coloured hero strip with the template type, title and star,
 * then the description, tags (curated categories visually distinguished),
 * owner and a Choose button.
 */
export function BlueprintCard({
  template,
  onChoose,
}: BlueprintCardProps): JSX.Element {
  const { isStarredEntity, toggleStarredEntity } = useStarredEntity(template);

  const { title, name, description, tags = [] } = template.metadata;
  const { type, owner, lifecycle } = template.spec;

  // Split tags into curated category tags vs. free-form tags
  const categoryTags = tags.filter(t => CATEGORY_IDS.has(t));
  const freeTags = tags.filter(t => !CATEGORY_IDS.has(t));

  const displayName = title ?? name;
  const displayType = type ? type.charAt(0).toUpperCase() + type.slice(1) : '';
  const ownerDisplay = formatOwner(owner);

  return (
    // Runs to the card's edges so the hero strip spans the full width.
    <Card style={{ padding: 0, height: '100%' }}>
      {/* ── Hero strip: the card's brand anchor, as on the original ──── */}
      <Box
        px="4"
        py="3"
        style={{
          minHeight: 56,
          backgroundColor: 'var(--bui-bg-solid)',
          color: 'var(--bui-fg-solid)',
        }}
      >
        <Flex align="start" gap="2">
          <Flex direction="column" gap="1" style={{ flex: 1, minWidth: 0 }}>
            {displayType && (
              <Text variant="body-small" style={{ color: 'inherit' }}>
                {displayType}
              </Text>
            )}
            <Text
              as="h3"
              variant="body-large"
              weight="bold"
              style={{ color: 'inherit' }}
            >
              {displayName}
            </Text>
          </Flex>
          <TooltipTrigger delay={300}>
            <ButtonIcon
              size="small"
              variant="tertiary"
              aria-label={isStarredEntity ? 'Unstar template' : 'Star template'}
              icon={
                isStarredEntity ? (
                  <RiStarFill size={16} />
                ) : (
                  <RiStarLine size={16} />
                )
              }
              onPress={toggleStarredEntity}
              style={{ color: 'inherit' }}
            />
            <Tooltip>
              {isStarredEntity ? 'Remove from favorites' : 'Add to favorites'}
            </Tooltip>
          </TooltipTrigger>
        </Flex>
      </Box>

      {/* ── Body ───────────────────────────────────────────────────────── */}
      <Flex direction="column" gap="2" px="4" py="3" style={{ flex: 1 }}>
        {description && (
          <Text
            as="p"
            color="secondary"
            style={{
              // Clamp to 3 lines
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {description}
          </Text>
        )}

        {/* Curated category tags (visually distinct) */}
        {categoryTags.length > 0 && (
          <Flex gap="1" style={WRAP}>
            {categoryTags.map(tag => (
              <Tag key={tag} tone="category">
                {tag}
              </Tag>
            ))}
          </Flex>
        )}

        {/* Free-form tags + lifecycle */}
        {(freeTags.length > 0 || lifecycle) && (
          <Flex gap="1" style={WRAP}>
            {lifecycle && <Tag tone="lifecycle">{lifecycle}</Tag>}
            {freeTags.slice(0, 4).map(tag => (
              <Tag key={tag} tone="neutral">
                {tag}
              </Tag>
            ))}
          </Flex>
        )}
      </Flex>

      {/* ── Footer ─────────────────────────────────────────────────────── */}
      <Flex
        align="center"
        justify="between"
        gap="2"
        px="4"
        py="2"
        style={{ borderTop: '1px solid var(--bui-border-1)' }}
      >
        <Text
          variant="body-small"
          color="secondary"
          truncate
          title={ownerDisplay}
          style={{ maxWidth: '55%' }}
        >
          {ownerDisplay}
        </Text>
        <Button variant="secondary" size="small" onPress={onChoose}>
          Choose
        </Button>
      </Flex>
    </Card>
  );
}

// ── List-mode row ─────────────────────────────────────────────────────────────

/**
 * Compact single-line representation of a template, used when the user
 * switches the Templates page to list view. Shares its API with
 * `BlueprintCard` so the two visual modes are interchangeable. The whole row
 * is a BUI interactive card that chooses the template.
 */
export function BlueprintListRow({
  template,
  onChoose,
}: BlueprintCardProps): JSX.Element {
  const tags = template.metadata.tags ?? [];
  const displayName = template.metadata.title ?? template.metadata.name;

  return (
    <Card onPress={onChoose} label={`Choose ${displayName}`}>
      <Flex align="center" gap="4">
        <Text weight="bold" style={{ flex: 1, minWidth: 0 }}>
          {displayName}
        </Text>
        {template.metadata.description && (
          <Text
            color="secondary"
            truncate
            title={template.metadata.description}
            style={{ flex: 2, minWidth: 0 }}
          >
            {template.metadata.description}
          </Text>
        )}
        {tags.length > 0 && (
          <Flex gap="1">
            {tags.slice(0, 3).map(tag => (
              <Tag key={tag} tone="neutral">
                {tag}
              </Tag>
            ))}
          </Flex>
        )}
      </Flex>
    </Card>
  );
}
